import { useTranslations } from 'next-intl';
import cx from 'classnames';
import { CatHeading, CatLoadingIndicator } from 'components/blocks-components';
import {
  Conditional,
  MyEquipmentSearchResults,
} from 'components/pcc-components';
import { EquipmentSuggestionsResultsProps } from 'components/my-equipment/types';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { SEARCH_RESULT_LIMIT } from 'components/my-equipment/constants';
import { QUERY_FETCH_STATUS } from 'constants/common';
import styles from './style.module.scss';

const EquipmentSuggestionsLoader = ({ isSmallViewport, label }) => (
  <div
    className={cx(
      'd-flex flex-column justify-content-center align-items-center',
      {
        [styles['loading-container']]: !isSmallViewport,
        'my-4 py-3': isSmallViewport,
      }
    )}
  >
    <CatLoadingIndicator
      className="mb-3"
      data-testid="equipment-suggestions-loader"
    />
    <CatHeading variant="label">{label}</CatHeading>
  </div>
);

export const EquipmentSuggestionsResults = ({
  error,
  fetchStatus,
  isSmallViewport,
  isFetching,
  onEquipmentSelected,
  searchTerm,
  suggestions,
}: EquipmentSuggestionsResultsProps) => {
  const t = useTranslations();
  const showModelNoMatchError =
    !isFetching &&
    fetchStatus === QUERY_FETCH_STATUS.ERROR &&
    error?.message === 'MEQ_MODEL_NO_MATCH';
  return (
    <Conditional test={searchTerm.length >= 2}>
      <Conditional test={isFetching}>
        <EquipmentSuggestionsLoader
          isSmallViewport={isSmallViewport}
          label={t('MEQ_LOADING_RESULTS')}
        />
      </Conditional>
      <Conditional
        test={!isFetching && fetchStatus === QUERY_FETCH_STATUS.SUCCESS}
      >
        <Conditional test={!isEmpty(suggestions?.equipments)}>
          <MyEquipmentSearchResults
            data-testid="equipment-suggestions-list"
            limit={SEARCH_RESULT_LIMIT.MATCHING}
            list={suggestions?.equipments}
            onSelectListItem={onEquipmentSelected}
            searchTerm={searchTerm}
          />
        </Conditional>
        <Conditional
          test={
            isEmpty(suggestions?.equipments) &&
            !isEmpty(suggestions?.otherEquipments)
          }
        >
          <div className="p-2 ps-3">
            <CatHeading variant="label">{t('MEQ_NO_EXACT_MATCH')}</CatHeading>
          </div>
        </Conditional>
        <Conditional test={!isEmpty(suggestions?.otherEquipments)}>
          <div className={`${styles['results-title']} p-1 ps-3`}>
            <CatHeading variant="body">{t('MEQ_OTHER_SUGGESTIONS')}</CatHeading>
          </div>
          <MyEquipmentSearchResults
            data-testid="equipment-suggestions-list"
            limit={SEARCH_RESULT_LIMIT.MATCHING}
            list={suggestions?.otherEquipments}
            onSelectListItem={onEquipmentSelected}
            searchTerm={searchTerm}
          />
        </Conditional>
        <Conditional
          test={
            !isEmpty(suggestions?.equipments) ||
            !isEmpty(suggestions?.otherEquipments)
          }
        >
          <div className="text-center mx-5 my-2">
            <CatHeading variant="body">
              {t('MEQ_MODEL_TRY_REFINING')}
            </CatHeading>
          </div>
        </Conditional>
      </Conditional>
      <Conditional test={showModelNoMatchError}>
        <div className="mx-3 my-2">
          <CatHeading color="error" variant="body-sm">
            {t('MEQ_MODEL_NO_MATCH')}
          </CatHeading>
        </div>
      </Conditional>
    </Conditional>
  );
};
